import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Tooltip, alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as BackIcon,
    KeyboardArrowUp as UpIcon,
    KeyboardArrowDown as DownIcon,
    // Ikony pro kategorie inventáře
    Air,
    Waves,
    Link as LinkIcon,
    Shield,
    Radio,
    Build,
    WaterDrop,
    MedicalServices,
    Lightbulb,
    Speed,
    FitnessCenter,
    Key,
    MoreHoriz,
    Checkroom,
    ElectricalServices,
    LocalFireDepartment,
    Handyman,
    Cable,
    Visibility,
    Construction,
    Inventory2,
    Category,
    HelpOutline,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useInventarKategorieQuery,
    useCreateKategorieMutation,
    useUpdateKategorieMutation,
    useDeleteKategorieMutation,
    useReorderKategorieMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import type { InventarKategorie } from '../../api/JsdhNotifikaceService/generated/models/Inventar';

// Mapa dostupných ikon pro kategorie inventáře
const ICON_OPTIONS: { value: string; label: string; icon: React.ReactElement }[] = [
    { value: 'air', label: 'Dýchací', icon: <Air /> },
    { value: 'waves', label: 'Hadice', icon: <Waves /> },
    { value: 'link', label: 'Lana', icon: <LinkIcon /> },
    { value: 'shield', label: 'Ochrana', icon: <Shield /> },
    { value: 'radio', label: 'Komunikace', icon: <Radio /> },
    { value: 'build', label: 'Nářadí', icon: <Build /> },
    { value: 'water_drop', label: 'Voda', icon: <WaterDrop /> },
    { value: 'medical_services', label: 'Zdravotní', icon: <MedicalServices /> },
    { value: 'lightbulb', label: 'Světlo', icon: <Lightbulb /> },
    { value: 'speed', label: 'Měřidla', icon: <Speed /> },
    { value: 'fitness_center', label: 'Sport', icon: <FitnessCenter /> },
    { value: 'key', label: 'Klíče', icon: <Key /> },
    { value: 'checkroom', label: 'Výstroj', icon: <Checkroom /> },
    { value: 'electrical_services', label: 'Elektro', icon: <ElectricalServices /> },
    { value: 'local_fire_department', label: 'Oheň', icon: <LocalFireDepartment /> },
    { value: 'handyman', label: 'Opravy', icon: <Handyman /> },
    { value: 'cable', label: 'Kabely', icon: <Cable /> },
    { value: 'visibility', label: 'Detekce', icon: <Visibility /> },
    { value: 'construction', label: 'Stavba', icon: <Construction /> },
    { value: 'inventory_2', label: 'Inventář', icon: <Inventory2 /> },
    { value: 'category', label: 'Obecné', icon: <Category /> },
    { value: 'more_horiz', label: 'Ostatní', icon: <MoreHoriz /> },
];

/** Vrátí React element ikony podle názvu z DB, nebo fallback */
export const getKategorieIcon = (iconName?: string): React.ReactElement => {
    const found = ICON_OPTIONS.find(o => o.value === iconName);
    return found ? found.icon : <HelpOutline />;
};

const InventarKategoriePage: React.FC = () => {
    const navigate = useNavigate();
    const { data: kategorie, isLoading } = useInventarKategorieQuery();
    const createMutation = useCreateKategorieMutation();
    const updateMutation = useUpdateKategorieMutation();
    const deleteMutation = useDeleteKategorieMutation();
    const reorderMutation = useReorderKategorieMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingPoradi, setEditingPoradi] = useState<number | null>(null);
    const [formNazev, setFormNazev] = useState('');
    const [formPopis, setFormPopis] = useState('');
    const [formIcon, setFormIcon] = useState('');

    const resetForm = () => {
        setFormNazev('');
        setFormPopis('');
        setFormIcon('');
        setEditingId(null);
        setEditingPoradi(null);
    };

    const openCreate = () => {
        resetForm();
        setDialogOpen(true);
    };

    const openEdit = (k: InventarKategorie) => {
        setEditingId(k.id);
        setEditingPoradi(k.poradi);
        setFormNazev(k.nazev);
        setFormPopis(k.popis ?? '');
        setFormIcon(k.icon ?? '');
        setDialogOpen(true);
    };

    const handleSubmit = () => {
        if (!formNazev) return;
        const base = { nazev: formNazev, popis: formPopis || undefined, icon: formIcon || undefined };
        if (editingId) {
            // Update nesmí měnit pořadí — posíláme původní hodnotu, jinak BE přesune kategorii na konec
            const data = { ...base, poradi: editingPoradi ?? undefined };
            updateMutation.mutate({ id: editingId, data }, { onSuccess: () => { setDialogOpen(false); resetForm(); } });
        } else {
            const data = { ...base, poradi: (kategorie?.length ?? 0) + 1 };
            createMutation.mutate(data, { onSuccess: () => { setDialogOpen(false); resetForm(); } });
        }
    };

    const handleMoveUp = (index: number) => {
        if (!kategorie || index <= 0) return;
        const ids = kategorie.map(k => k.id);
        [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
        reorderMutation.mutate(ids);
    };

    const handleMoveDown = (index: number) => {
        if (!kategorie || index >= kategorie.length - 1) return;
        const ids = kategorie.map(k => k.id);
        [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
        reorderMutation.mutate(ids);
    };

    const handleDelete = (k: InventarKategorie) => {
        if (k.pocetPolozek > 0) {
            alert(`Nelze smazat kategorii "${k.nazev}" — obsahuje ${k.pocetPolozek} položek.`);
            return;
        }
        if (window.confirm(`Opravdu chcete smazat kategorii "${k.nazev}"?`)) {
            deleteMutation.mutate(k.id);
        }
    };

    return (
        <Box>
            <PageHeader
                title="Kategorie inventáře"
                icon={<BackIcon sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/inventar')} />}
                action={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                        Nová kategorie
                    </Button>
                }
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={50}></TableCell>
                                    <TableCell>Název</TableCell>
                                    <TableCell>Popis</TableCell>
                                    <TableCell width={100} align="center">Položek</TableCell>
                                    <TableCell width={80} align="center">Pořadí</TableCell>
                                    <TableCell width={100} align="right">Akce</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {kategorie?.map((k, index) => (
                                    <TableRow key={k.id}>
                                        <TableCell>
                                            <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                                                {getKategorieIcon(k.icon)}
                                            </Box>
                                        </TableCell>
                                        <TableCell><Typography fontWeight={500}>{k.nazev}</Typography></TableCell>
                                        <TableCell><Typography variant="body2" color="text.secondary">{k.popis ?? '—'}</Typography></TableCell>
                                        <TableCell align="center">{k.pocetPolozek}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" disabled={index === 0 || reorderMutation.isPending} onClick={() => handleMoveUp(index)}>
                                                <UpIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" disabled={index === (kategorie?.length ?? 0) - 1 || reorderMutation.isPending} onClick={() => handleMoveDown(index)}>
                                                <DownIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => openEdit(k)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(k)} disabled={k.pocetPolozek > 0}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!kategorie || !kategorie.length) && (
                                    <TableRow><TableCell colSpan={6} align="center">Žádné kategorie</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Upravit kategorii' : 'Nová kategorie'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Název" required value={formNazev} onChange={(e) => setFormNazev(e.target.value)} placeholder="Např. Elektrotechnika" />
                        <TextField label="Popis" value={formPopis} onChange={(e) => setFormPopis(e.target.value)} placeholder="Popis kategorie" />

                        {/* Výběr ikony z předpřipravených */}
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Ikona</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {ICON_OPTIONS.map(opt => (
                                    <Tooltip key={opt.value} title={opt.label}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setFormIcon(opt.value)}
                                            sx={{
                                                border: '2px solid',
                                                borderColor: formIcon === opt.value ? 'primary.main' : 'transparent',
                                                bgcolor: formIcon === opt.value ? (theme) => alpha(theme.palette.primary.main, 0.1) : undefined,
                                                borderRadius: 1,
                                                width: 44,
                                                height: 44,
                                            }}
                                        >
                                            {React.cloneElement(opt.icon as React.ReactElement<any>, {
                                                fontSize: 'small',
                                                color: formIcon === opt.value ? 'primary' : 'action',
                                            })}
                                        </IconButton>
                                    </Tooltip>
                                ))}
                            </Box>
                            {formIcon && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Vybráno: {ICON_OPTIONS.find(o => o.value === formIcon)?.label ?? formIcon}
                                </Typography>
                            )}
                        </Box>

                        {/* Pořadí se řídí tlačítky ↑↓ v tabulce, ne ručně */}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Zrušit</Button>
                    <Button variant="contained" onClick={handleSubmit}
                            disabled={!formNazev || createMutation.isPending || updateMutation.isPending}>
                        {editingId ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventarKategoriePage;
